'use client'

import { useState, ChangeEvent, useEffect } from 'react'
import { FileText, Save, ChevronDown, Book, X, ImageIcon } from 'lucide-react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import type { SceneInput } from '@creanizer/types'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'
import { usePlotStore } from '../../../../stores/plotStore'

interface SceneFormStandardProps {
  input: SceneInput
  onChange: (input: SceneInput) => void
}

interface UploadedFile {
  id: string
  name: string
  content: string
}

interface UploadedImage {
  id: string
  name: string
  data: string
  size: number
}

export function SceneFormStandard({ input, onChange }: SceneFormStandardProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [showCustomTypes, setShowCustomTypes] = useState(false)
  const [showCustomGenres, setShowCustomGenres] = useState(false)
  const [showCustomAtmospheres, setShowCustomAtmospheres] = useState(false)
  const [showPlotSelector, setShowPlotSelector] = useState(false)
  
  // 読点区切り入力用の内部state（入力中は文字列として保持）
  const [themesText, setThemesText] = useState(input.themes?.join('、') || '')
  const [focusElementsText, setFocusElementsText] = useState(input.focusElements?.join('、') || '')
  const [avoidElementsText, setAvoidElementsText] = useState(input.avoidElements?.join('、') || '')
  
  const { 
    customValues, 
    addCustomValue, 
    getCustomValues, 
    useCustomValue,
    init 
  } = useCustomValuesStore()
  
  const { savedPlots, getPlot } = usePlotStore()
  
  // カスタム値ストアの初期化
  useEffect(() => {
    init()
  }, [])
  
  // 外部からのinput変更時に内部stateを同期
  useEffect(() => {
    setThemesText(input.themes?.join('、') || '')
    setFocusElementsText(input.focusElements?.join('、') || '')
    setAvoidElementsText(input.avoidElements?.join('、') || '')
  }, [input.themes, input.focusElements, input.avoidElements])

  // カスタム値を保存
  const saveCustomValue = async (type: 'sceneType' | 'genre' | 'atmosphere', value: string, label: string) => {
    try {
      await addCustomValue(type, value, label, 'scene')
      alert(`${label}を保存しました`)
    } catch (error) {
      console.error('Failed to save custom value:', error)
      alert('保存に失敗しました')
    }
  }
  
  // カスタム値を選択して使用
  const selectCustomValue = async (type: 'sceneType' | 'genre' | 'atmosphere', value: any) => {
    // 使用回数を更新
    await useCustomValue(value.id)
    
    // 値を設定
    if (type === 'sceneType') {
      onChange({ ...input, sceneType: 'custom', customType: value.value })
    } else if (type === 'genre') {
      onChange({ ...input, genre: 'custom', customGenre: value.value })
    } else if (type === 'atmosphere') {
      onChange({ ...input, atmosphere: 'custom', customAtmosphere: value.value })
    }
  }

  // テキストファイルアップロード処理（複数対応）
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const newFiles: UploadedFile[] = []

    for (const file of Array.from(files)) {
      try {
        const text = await file.text()
        newFiles.push({
          id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          content: text
        })
      } catch (error) {
        console.error('ファイル読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newFiles.length > 0) {
      const allFiles = [...uploadedFiles, ...newFiles]
      setUploadedFiles(allFiles)
      const allFilesContent = allFiles
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      onChange({ ...input, uploadedContent: allFilesContent || undefined })
    }

    e.target.value = ''
  }

  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)
    const allFilesContent = newFiles
      .map(file => `【ファイル: ${file.name}】\n${file.content}`)
      .join('\n\n')
    onChange({ ...input, uploadedContent: allFilesContent || undefined })
  }

  // 画像アップロード処理
  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const maxSize = 20 * 1024 * 1024 // 20MB制限
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

    const newImages: UploadedImage[] = []

    for (const file of Array.from(files)) {
      if (!validTypes.includes(file.type)) {
        alert(`${file.name} はサポートされていない形式です（対応: JPEG/PNG/GIF/WebP）`)
        continue
      }

      if (file.size > maxSize) {
        alert(`${file.name} はファイルサイズが大きすぎます (最大20MB)`)
        continue
      }

      try {
        const reader = new FileReader()
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        newImages.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          data: base64,
          size: file.size
        })
      } catch (error) {
        console.error('画像読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newImages.length > 0) {
      const allImages = [...uploadedImages, ...newImages].slice(0, 5) // 最大5枚まで
      setUploadedImages(allImages)
      onChange({ ...input, images: allImages.map(img => img.data) })
    }

    e.target.value = ''
  }

  const handleImageRemove = (imageId: string) => {
    const newImages = uploadedImages.filter(img => img.id !== imageId)
    setUploadedImages(newImages)
    onChange({ ...input, images: newImages.length > 0 ? newImages.map(img => img.data) : undefined })
  }

  return (
    <div className="space-y-4">
      {/* シーンタイプ選択 */}
      <div>
        <Label htmlFor="sceneType">シーンタイプ</Label>
        <Select
          value={input.sceneType || 'description'}
          onValueChange={(value) => onChange({ ...input, sceneType: value as any })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="description">描写</SelectItem>
            <SelectItem value="action">アクション</SelectItem>
            <SelectItem value="dialogue">会話</SelectItem>
            <SelectItem value="emotional">感情的</SelectItem>
            <SelectItem value="transition">転換</SelectItem>
            <SelectItem value="climax">クライマックス</SelectItem>
            <SelectItem value="opening">オープニング</SelectItem>
            <SelectItem value="ending">エンディング</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
            {/* 短編小説タイプ */}
            <SelectItem value="complete_story">📚 完結型短編</SelectItem>
            <SelectItem value="twist_ending">🎭 どんでん返し短編</SelectItem>
            <SelectItem value="slice_of_life">☕ 日常系短編</SelectItem>
            <SelectItem value="flash_fiction">⚡ 超短編（フラッシュ）</SelectItem>
            <SelectItem value="fairy_tale_complete">🏰 完結型童話</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* 短編小説タイプの説明 */}
      {['complete_story', 'twist_ending', 'slice_of_life', 'flash_fiction', 'fairy_tale_complete'].includes(input.sceneType || '') && (
        <Alert>
          <AlertDescription>
            {input.sceneType === 'complete_story' && '起承転結を含む一話完結の短編小説を生成します。推奨文字数: 5000〜10000文字'}
            {input.sceneType === 'twist_ending' && '意外な結末で読者を驚かせる短編を生成します。伏線を巧みに配置します。'}
            {input.sceneType === 'slice_of_life' && '日常の小さな出来事を丁寧に描写した短編を生成します。余韻を残す結末になります。'}
            {input.sceneType === 'flash_fiction' && '極限まで無駄を削ぎ落とした超短編を生成します。推奨: 1000文字以内'}
            {input.sceneType === 'fairy_tale_complete' && '子供向けのわかりやすい童話を生成します。教訓やメッセージを含みます。'}
          </AlertDescription>
        </Alert>
      )}

      {/* カスタムタイプ入力 */}
      {input.sceneType === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="customType">カスタムシーンタイプ</Label>
          <div className="flex gap-2">
            <Input
              id="customType"
              value={input.customType || ''}
              onChange={(e) => onChange({ ...input, customType: e.target.value })}
              placeholder="独自のシーンタイプを入力"
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (input.customType) {
                  saveCustomValue('sceneType', input.customType, input.customType)
                }
              }}
              disabled={!input.customType}
            >
              <Save className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowCustomTypes(!showCustomTypes)}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          {showCustomTypes && (
            <div className="border rounded p-2 max-h-32 overflow-y-auto">
              {getCustomValues('sceneType', 'scene').length === 0 ? (
                <p className="text-sm text-gray-500">保存されたシーンタイプはありません</p>
              ) : (
                <div className="space-y-1">
                  {getCustomValues('sceneType', 'scene').map((value) => (
                    <button
                      key={value.id}
                      type="button"
                      className="block w-full text-left p-1 hover:bg-gray-100 rounded text-sm"
                      onClick={() => selectCustomValue('sceneType', value)}
                    >
                      {value.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* シーン概要 */}
      <div>
        <Label htmlFor="sceneSummary">シーンの概要（文字数制限なし）</Label>
        <Textarea
          id="sceneSummary"
          value={input.sceneSummary || ''}
          onChange={(e) => onChange({ ...input, sceneSummary: e.target.value })}
          placeholder="このシーンで何が起こるかを詳しく説明してください"
          rows={4}
        />
      </div>

      {/* 場所 */}
      <div>
        <Label htmlFor="sceneLocation">場所</Label>
        <Input
          id="sceneLocation"
          value={input.sceneLocation || ''}
          onChange={(e) => onChange({ ...input, sceneLocation: e.target.value })}
          placeholder="例：古城の大広間、未来都市の中心街"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 時間帯 */}
        <div>
          <Label htmlFor="timeOfDay">時間帯</Label>
          <Input
            id="timeOfDay"
            value={input.timeOfDay || ''}
            onChange={(e) => onChange({ ...input, timeOfDay: e.target.value })}
            placeholder="例：夜明け前、真昼"
          />
        </div>

        {/* 天候 */}
        <div>
          <Label htmlFor="weather">天候・環境</Label>
          <Input
            id="weather"
            value={input.weather || ''}
            onChange={(e) => onChange({ ...input, weather: e.target.value })}
            placeholder="例：激しい雷雨、穏やかな晴天"
          />
        </div>
      </div>

      {/* 雰囲気 */}
      <div>
        <Label htmlFor="atmosphere">雰囲気</Label>
        <Select
          value={input.atmosphere || 'neutral'}
          onValueChange={(value) => onChange({ ...input, atmosphere: value as any })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tense">緊張感のある</SelectItem>
            <SelectItem value="peaceful">平和な</SelectItem>
            <SelectItem value="mysterious">神秘的な</SelectItem>
            <SelectItem value="romantic">ロマンティックな</SelectItem>
            <SelectItem value="comedic">コメディ調の</SelectItem>
            <SelectItem value="dark">ダークな</SelectItem>
            <SelectItem value="epic">壮大な</SelectItem>
            <SelectItem value="melancholic">メランコリックな</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* カスタム雰囲気 */}
      {input.atmosphere === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="customAtmosphere">カスタム雰囲気</Label>
          <div className="flex gap-2">
            <Input
              id="customAtmosphere"
              value={input.customAtmosphere || ''}
              onChange={(e) => onChange({ ...input, customAtmosphere: e.target.value })}
              placeholder="独自の雰囲気を入力"
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (input.customAtmosphere) {
                  saveCustomValue('atmosphere', input.customAtmosphere, input.customAtmosphere)
                }
              }}
              disabled={!input.customAtmosphere}
            >
              <Save className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowCustomAtmospheres(!showCustomAtmospheres)}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          {showCustomAtmospheres && (
            <div className="border rounded p-2 max-h-32 overflow-y-auto">
              {getCustomValues('atmosphere', 'scene').length === 0 ? (
                <p className="text-sm text-gray-500">保存された雰囲気はありません</p>
              ) : (
                <div className="space-y-1">
                  {getCustomValues('atmosphere', 'scene').map((value) => (
                    <button
                      key={value.id}
                      type="button"
                      className="block w-full text-left p-1 hover:bg-gray-100 rounded text-sm"
                      onClick={() => selectCustomValue('atmosphere', value)}
                    >
                      {value.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ジャンル選択 */}
      <div>
        <Label htmlFor="genre">ジャンル</Label>
        <Select
          value={input.genre || 'modern'}
          onValueChange={(value) => onChange({ ...input, genre: value as any })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fantasy">ファンタジー</SelectItem>
            <SelectItem value="sf">SF</SelectItem>
            <SelectItem value="modern">現代</SelectItem>
            <SelectItem value="mystery">ミステリー</SelectItem>
            <SelectItem value="romance">ロマンス</SelectItem>
            <SelectItem value="horror">ホラー</SelectItem>
            <SelectItem value="historical">歴史</SelectItem>
            <SelectItem value="comedy">コメディ</SelectItem>
            <SelectItem value="literary">文学</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* カスタムジャンル */}
      {input.genre === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="customGenre">カスタムジャンル</Label>
          <div className="flex gap-2">
            <Input
              id="customGenre"
              value={input.customGenre || ''}
              onChange={(e) => onChange({ ...input, customGenre: e.target.value })}
              placeholder="独自のジャンルを入力"
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (input.customGenre) {
                  saveCustomValue('genre', input.customGenre, input.customGenre)
                }
              }}
              disabled={!input.customGenre}
            >
              <Save className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowCustomGenres(!showCustomGenres)}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          {showCustomGenres && (
            <div className="border rounded p-2 max-h-32 overflow-y-auto">
              {getCustomValues('genre', 'scene').length === 0 ? (
                <p className="text-sm text-gray-500">保存されたジャンルはありません</p>
              ) : (
                <div className="space-y-1">
                  {getCustomValues('genre', 'scene').map((value) => (
                    <button
                      key={value.id}
                      type="button"
                      className="block w-full text-left p-1 hover:bg-gray-100 rounded text-sm"
                      onClick={() => selectCustomValue('genre', value)}
                    >
                      {value.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* プロット連携 */}
      <div className="space-y-2">
        <Label>プロット連携（オプション）</Label>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowPlotSelector(!showPlotSelector)}
          className="w-full justify-start"
        >
          <Book className="mr-2 h-4 w-4" />
          {input.selectedPlotId ? 
            `選択済み: ${getPlot(input.selectedPlotId)?.title || 'プロット'}` + 
            (input.selectedChapterNumber ? ` - 第${input.selectedChapterNumber}章` : '') : 
            'プロットから章を選択'
          }
        </Button>
        
        {showPlotSelector && (
          <div className="border rounded-lg p-4 space-y-3">
            {savedPlots.length === 0 ? (
              <p className="text-sm text-gray-500">保存されたプロットがありません</p>
            ) : (
              <>
                {/* プロット選択 */}
                <div>
                  <Label htmlFor="plotSelect">プロットを選択</Label>
                  <Select
                    value={input.selectedPlotId || ''}
                    onValueChange={(value) => {
                      const plot = getPlot(value)
                      if (plot) {
                        onChange({ 
                          ...input, 
                          selectedPlotId: value,
                          selectedChapterNumber: undefined,
                          plotContext: undefined
                        })
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="プロットを選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedPlots.map((plot) => (
                        <SelectItem key={plot.id} value={plot.id}>
                          {plot.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* 章選択 */}
                {input.selectedPlotId && (
                  <div>
                    <Label htmlFor="chapterSelect">章を選択</Label>
                    <Select
                      value={input.selectedChapterNumber?.toString() || ''}
                      onValueChange={(value) => {
                        const plot = getPlot(input.selectedPlotId!)
                        if (plot) {
                          const chapterNum = parseInt(value)
                          const chapter = plot.result.structure.find(ch => ch.number === chapterNum)
                          if (chapter) {
                            onChange({ 
                              ...input, 
                              selectedChapterNumber: chapterNum,
                              plotContext: `章タイトル: ${chapter.title}\n\n概要: ${chapter.summary}`
                            })
                          }
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="章を選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        {getPlot(input.selectedPlotId)?.result.structure.map((chapter) => (
                          <SelectItem key={chapter.number} value={chapter.number.toString()}>
                            第{chapter.number}章: {chapter.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* 選択された章の情報表示 */}
                {input.plotContext && (
                  <div className="bg-gray-50 p-3 rounded text-sm">
                    <p className="font-semibold mb-1">選択された章の情報:</p>
                    <p className="whitespace-pre-wrap text-gray-700">{input.plotContext}</p>
                  </div>
                )}
                
                {/* クリアボタン */}
                {input.selectedPlotId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      onChange({ 
                        ...input, 
                        selectedPlotId: undefined,
                        selectedChapterNumber: undefined,
                        plotContext: undefined
                      })
                      setShowPlotSelector(false)
                    }}
                  >
                    選択をクリア
                  </Button>
                )}
              </>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          プロットジェネレータで作成したプロットの特定の章に基づいてシーンを生成できます
        </p>
      </div>

      {/* 文字数設定 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>目標文字数</Label>
          <span className="text-sm text-muted-foreground">{input.targetLength || 1500}文字</span>
        </div>
        <Slider
          value={[input.targetLength || 1500]}
          onValueChange={([value]) => onChange({ ...input, targetLength: value })}
          min={100}
          max={10000}
          step={100}
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>100文字</span>
          <span>10000文字</span>
        </div>
      </div>

      {/* 簡易長さ選択 */}
      <div>
        <Label>簡易長さ選択</Label>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <Button
            type="button"
            variant={input.lengthType === 'short' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange({ ...input, lengthType: 'short', targetLength: 500 })}
          >
            短編
          </Button>
          <Button
            type="button"
            variant={input.lengthType === 'medium' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange({ ...input, lengthType: 'medium', targetLength: 1500 })}
          >
            中編
          </Button>
          <Button
            type="button"
            variant={input.lengthType === 'long' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange({ ...input, lengthType: 'long', targetLength: 3000 })}
          >
            長編
          </Button>
          <Button
            type="button"
            variant={input.lengthType === 'epic' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange({ ...input, lengthType: 'epic', targetLength: 5000 })}
          >
            超長編
          </Button>
        </div>
      </div>

      {/* テキストファイルアップロード（複数対応） */}
      <div className="space-y-2">
        <Label htmlFor="fileUpload">参考資料ファイル（オプション）</Label>
        <Input
          id="fileUpload"
          type="file"
          accept=".txt,.md,.json"
          multiple
          onChange={handleFileUpload}
        />
        <p className="text-xs text-muted-foreground">
          テキストファイル（.txt/.md/.json）を複数アップロードして参考資料として使用できます
        </p>

        {/* アップロード済みファイル一覧 */}
        {uploadedFiles.length > 0 && (
          <div className="space-y-2 mt-2">
            {uploadedFiles.map(file => (
              <div key={file.id} className="flex items-center gap-2 p-2 bg-muted rounded">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm flex-1 truncate">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFileRemove(file.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 画像アップロード */}
      <div className="space-y-2">
        <Label htmlFor="imageUpload">インスピレーション画像（オプション）</Label>
        <Input
          id="imageUpload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          multiple
          onChange={handleImageUpload}
        />
        <p className="text-xs text-muted-foreground">
          シーンの雰囲気・風景の参考として画像をアップロードできます（対応形式: JPEG/PNG/GIF/WebP、最大5枚、各20MBまで）
        </p>

        {/* アップロード済み画像プレビュー */}
        {uploadedImages.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {uploadedImages.map(img => (
              <div key={img.id} className="relative group">
                <img
                  src={img.data}
                  alt={img.name}
                  className="w-full h-20 object-cover rounded border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleImageRemove(img.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
                <p className="text-xs text-muted-foreground truncate mt-1">{img.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 感情的トーン */}
      <div>
        <Label htmlFor="emotionalTone">感情的トーン（オプション）</Label>
        <Input
          id="emotionalTone"
          value={input.emotionalTone || ''}
          onChange={(e) => onChange({ ...input, emotionalTone: e.target.value })}
          placeholder="例：希望に満ちた、絶望的な、ノスタルジック"
        />
      </div>

      {/* テーマ */}
      <div>
        <Label htmlFor="themes">テーマ（読点区切り）</Label>
        <Input
          id="themes"
          value={themesText}
          onChange={(e) => setThemesText(e.target.value)}
          onBlur={() => {
            // フォーカスが外れた時に配列に変換
            onChange({ 
              ...input, 
              themes: themesText
                .split(/[,、，]/)  // 半角カンマ、全角読点、全角カンマに対応
                .map(t => t.trim())
                .filter(Boolean)
            })
          }}
          placeholder="例：友情、裏切り、成長"
        />
      </div>

      {/* 重点要素 */}
      <div>
        <Label htmlFor="focusElements">重点を置く要素（読点区切り）</Label>
        <Input
          id="focusElements"
          value={focusElementsText}
          onChange={(e) => setFocusElementsText(e.target.value)}
          onBlur={() => {
            onChange({ 
              ...input, 
              focusElements: focusElementsText
                .split(/[,、，]/)
                .map(t => t.trim())
                .filter(Boolean)
            })
          }}
          placeholder="例：心理描写、風景描写、アクション"
        />
      </div>

      {/* 避ける要素 */}
      <div>
        <Label htmlFor="avoidElements">避ける要素（読点区切り）</Label>
        <Input
          id="avoidElements"
          value={avoidElementsText}
          onChange={(e) => setAvoidElementsText(e.target.value)}
          onBlur={() => {
            onChange({ 
              ...input, 
              avoidElements: avoidElementsText
                .split(/[,、，]/)
                .map(t => t.trim())
                .filter(Boolean)
            })
          }}
          placeholder="例：暴力描写、専門用語"
        />
      </div>

      {/* 追加設定 */}
      <div>
        <Label htmlFor="additionalSettings">追加設定</Label>
        <Textarea
          id="additionalSettings"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="その他の詳細な設定や要望"
          rows={3}
        />
      </div>
    </div>
  )
}